import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import Sparkline from './Sparkline';

interface KpiSeries {
  credits: number[];
  debits: number[];
  savings: number[];
  /** Libellés de mois alignés sur les trois séries — activent les infobulles. */
  labels: string[];
}

interface BalanceHeroProps {
  /** Libellé de surtitre (ex. « Solde du compte commun »). */
  label: string;
  balance: number;
  /** Variation vs période précédente, en % et en valeur absolue. */
  changePercent?: number;
  change?: number;
  previousLabel?: string;
  credits: number;
  debits: number;
  savingsRate: number;
  series: KpiSeries;
  formatCurrency: (amount: number) => string;
  /** Sélecteur de compte unique du dashboard (rendu à côté du surtitre). */
  accountSelector?: React.ReactNode;
}

/**
 * Bloc héros du dashboard : le solde en très grand, sa variation, puis trois
 * KPI (crédits / débits / taux d'épargne) séparés par des filets et surmontés
 * d'une micro-courbe.
 *
 * Remplace la rangée de quatre stat-cards identiques : une seule information
 * domine (le solde), les autres deviennent secondaires — c'est la hiérarchie
 * qui rend l'écran lisible.
 */
export default function BalanceHero({
  label,
  balance,
  changePercent,
  change,
  previousLabel,
  credits,
  debits,
  savingsRate,
  series,
  formatCurrency,
  accountSelector,
}: BalanceHeroProps) {
  const { t, i18n } = useTranslation('dashboard');
  const hasDelta = changePercent !== undefined && Number.isFinite(changePercent);
  const up = (changePercent ?? 0) >= 0;
  const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-US';

  // Le taux d'épargne est un pourcentage : il ne peut pas passer par
  // formatCurrency. Séparateur décimal localisé (virgule en fr, point en en).
  const formatPercent = (value: number) =>
    `${new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value)} %`;

  // Le solde est composé « chiffre + devise » pour pouvoir cadrer la devise
  // plus petite et centrée, comme dans la maquette.
  const amount = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(balance);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="flex items-center justify-between gap-3 mb-2.5">
          <div className="eyebrow">{label}</div>
          {accountSelector}
        </div>
        <div className="balance-fig num">
          <span>{amount}</span>
          <span className="cur">€</span>
        </div>
        {hasDelta && (
          <div className="flex items-center gap-3 mt-3.5 flex-wrap">
            <span className={clsx('delta', up ? 'up' : 'down')}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden>
                <path d={up ? 'M6 14l6-6 6 6' : 'M6 10l6 6 6-6'} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {up ? '+' : '−'}{Math.abs(changePercent!).toFixed(1).replace('.', ',')} %
            </span>
            {change !== undefined && previousLabel && (
              <span className="text-xs" style={{ color: 'var(--text-3)' }}>
                {t('hero.vsPrevious', { amount: formatCurrency(Math.abs(change)), sign: up ? '+' : '−', period: previousLabel })}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="kpis">
        <div className="kpi">
          <span className="k-lbl">{t('common.credits')}</span>
          <span className="k-val pos num">+{formatCurrency(credits)}</span>
          <Sparkline
            values={series.credits}
            labels={series.labels}
            color="var(--credit)"
            seriesLabel={t('common.credits')}
            formatValue={formatCurrency}
          />
        </div>
        <div className="kpi">
          <span className="k-lbl">{t('common.totalExpenses')}</span>
          <span className="k-val neg num">−{formatCurrency(debits)}</span>
          <Sparkline
            values={series.debits}
            labels={series.labels}
            color="var(--debit)"
            seriesLabel={t('common.totalExpenses')}
            formatValue={formatCurrency}
          />
        </div>
        <div className="kpi">
          <span className="k-lbl">{t('hero.savingsRate')}</span>
          <span className="k-val num">{formatPercent(savingsRate)}</span>
          <Sparkline
            values={series.savings}
            labels={series.labels}
            color="var(--pa)"
            seriesLabel={t('hero.savingsRate')}
            formatValue={formatPercent}
          />
        </div>
      </div>
    </div>
  );
}
